import React, { useContext, useState } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  IconButton,
} from "@mui/material";
import { CircularProgress } from "@material-ui/core";
import { Typography } from "@material-ui/core";
import clsx from "clsx";
import { useSnackbar } from "notistack";
import { viewportContext } from "../../../context/ViewportContext";

import { updateRecordPatientCare } from "../../../service/endpoint-manager";
import { patientCarePDFReport } from "./PatientCarePDFReport";

import styles from "../../../styles.module.css";
import DeleteIcon from "@mui/icons-material/Delete";
import ReplayIcon from "@mui/icons-material/Replay";

const LoadingSpinner = () => {
  return (
    <div
      style={{
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        textAlign: "center",
        // height: "30px",
        // width: "30vh",
      }}
    >
      <CircularProgress size={25} />
    </div>
  );
};

const formatCreatedAtDate = (createdAt) => {
  const date = new Date(createdAt);
  return date.toLocaleString("en-US", {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    hour12: true,
  });
};

const PatientCareTable = ({
  patientCareRecords,
  setPatientCareRecords,
  filteredPatientCareRecords,
  setFilteredPatientCareRecords,
  dataFetched,
  isDashboard,
  setIsDashboard,
}) => {
  const { screenType } = useContext(viewportContext);
  const { enqueueSnackbar } = useSnackbar();

  const [deletingRecordId, setDeletingRecordId] = useState(null);

  // delete records from the list - uses the update api
  const handleDelete = async (recordId) => {
    setDeletingRecordId(recordId);

    try {
      const response = await updateRecordPatientCare(recordId, {
        deleted: true,
      });
      if (response.status === 200) {
        const updatedRecords = patientCareRecords.filter(
          (record) => record.id !== recordId
        );
        setPatientCareRecords(updatedRecords);
        setDeletingRecordId(null);
        enqueueSnackbar("Record Deleted Successfully", {
          variant: "success",
        });
      } else {
        console.error("Failed to delete the record", response.data);
        setDeletingRecordId(null);
        enqueueSnackbar("Failed to delete record", {
          variant: "error",
        });
      }
    } catch (error) {
      console.error(
        "Error deleting the record:",
        error.response ? error.response.data : error
      );
      setDeletingRecordId(null);
    }
  };

  // to restore deleted records - uses update api
  const handleRestore = async (recordId) => {
    setDeletingRecordId(recordId);

    try {
      const response = await updateRecordPatientCare(recordId, {
        deleted: false,
      });
      if (response.status === 200) {
        const updatedRecords = patientCareRecords.filter(
          (record) => record.id !== recordId
        );
        setPatientCareRecords(updatedRecords);
        setDeletingRecordId(null);
        enqueueSnackbar("Record Restored Successfully", {
          variant: "success",
        });
      } else {
        console.error("Failed to restore the record", response.data);
        setDeletingRecordId(null);
        enqueueSnackbar("Failed to restore record", {
          variant: "error",
        });
      }
    } catch (error) {
      console.error(
        "Error restoring the record:",
        error.response ? error.response.data : error
      );
      setDeletingRecordId(null);
    }
  };

  return (
    <Paper className={styles.paperStyles}>
      <div>
        <div className={styles.titleButtonRow}>
          <Typography variant="h5">Audit Report Summary</Typography>
          <div className={styles.exportButtonContainer}>
            <div>
              <Button
                variant="contained"
                color="primary"
                className={clsx(
                  styles.primaryButton,
                  styles.complianceTableButtons
                )}
                onClick={() => setIsDashboard(!isDashboard)}
              >
                + New Observation
              </Button>
              <Button
                variant="contained"
                color="primary"
                className={clsx(
                  styles.secondaryButton,
                  styles.complianceTableButtons
                )}
                onClick={() => patientCarePDFReport(filteredPatientCareRecords)}
              >
                {screenType.isMobile ? "PDF" : "Export PDF"}
              </Button>
            </div>
          </div>
        </div>
        <TableContainer className={styles.narcoticsReportTableContainer}>
          {dataFetched ? (
            <Table stickyHeader aria-label="narcotics records">
              <TableHead>
                <TableRow className={styles.narcoticsReportHeader}>
                  {/* <TableCell>Created By</TableCell> */}
                  <TableCell>Date</TableCell>
                  <TableCell>Resident Name</TableCell>
                  <TableCell>Facility</TableCell>
                  <TableCell>Unit</TableCell>
                  <TableCell>Personal Care Audit</TableCell>
                  <TableCell>Resident Environment</TableCell>
                  <TableCell>Mobility Aids</TableCell>
                  <TableCell>PCC</TableCell>
                  <TableCell></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredPatientCareRecords.map((record, recordIndex) => (
                  <TableRow key={recordIndex}>
                    {/* <TableCell>{``}</TableCell> */}
                    <TableCell>
                      {formatCreatedAtDate(record.createdAt)}
                    </TableCell>
                    <TableCell>{record.residentName}</TableCell>
                    <TableCell>{record.facility}</TableCell>
                    <TableCell>{record.unit}</TableCell>
                    <TableCell>
                      {record.personalCareAudit.compliance.percentage >= 0.6
                        ? `Compliant (${(
                            record.personalCareAudit.compliance.percentage * 100
                          ).toFixed(2)}%)`
                        : `Non-Compliant (${(
                            record.personalCareAudit.compliance.percentage * 100
                          ).toFixed(2)}%)`}
                    </TableCell>
                    <TableCell>
                      {record.residentEnvironment.compliance.percentage >= 0.6
                        ? `Compliant (${(
                            record.residentEnvironment.compliance.percentage *
                            100
                          ).toFixed(2)}%)`
                        : `Non-Compliant (${(
                            record.residentEnvironment.compliance.percentage *
                            100
                          ).toFixed(2)})`}
                    </TableCell>
                    <TableCell>
                      {record.mobilityAids.compliance.percentage >= 0.6
                        ? `Compliant (${(
                            record.mobilityAids.compliance.percentage * 100
                          ).toFixed(2)}%)`
                        : `Non-Compliant (${(
                            record.mobilityAids.compliance.percentage * 100
                          ).toFixed(2)})`}
                    </TableCell>
                    <TableCell>
                      {record.pcc.compliance.percentage >= 0.6
                        ? `Compliant (${(
                            record.pcc.compliance.percentage * 100
                          ).toFixed(2)}%)`
                        : `Non-Compliant (${(
                            record.pcc.compliance.percentage * 100
                          ).toFixed(2)})`}
                    </TableCell>
                    <TableCell>
                      {deletingRecordId === record.id ? (
                        <LoadingSpinner />
                      ) : record.deleted ? (
                        <IconButton
                          onClick={() => handleRestore(record.id)}
                          aria-label="restore"
                        >
                          <ReplayIcon />
                        </IconButton>
                      ) : (
                        <IconButton
                          onClick={() => handleDelete(record.id)}
                          aria-label="delete"
                        >
                          <DeleteIcon />
                        </IconButton>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          ) : (
            <p style={{ textAlign: "center", marginTop: "30px" }}>
              Please Wait While The Data is Being Fetched ...
            </p>
          )}
        </TableContainer>
      </div>
    </Paper>
  );
};

export default PatientCareTable;
